/**
 * Identificadores UUID v7.
 *
 * Por qué v7 y no v4 (DATABASE.md §1):
 *  - Ordenable por tiempo → mucho mejor localidad en índices B-tree que v4,
 *    que fragmenta el índice al insertar aleatoriamente.
 *  - Generable en el dispositivo → imprescindible para el alta de equipos
 *    offline (OFFLINE-ARCHITECTURE.md §1): sin identidad local no hay
 *    creación sin conexión.
 *  - Opaco hacia fuera → no expone contadores ni volumen de negocio.
 *
 * Implementación sin dependencias para poder usarse también en React Native.
 * Layout (RFC 9562):
 *   48 bits  timestamp Unix en ms
 *    4 bits  versión (0111)
 *   12 bits  contador/aleatorio  → monotonía dentro del mismo milisegundo
 *    2 bits  variante (10)
 *   62 bits  aleatorio
 */

const HEX: readonly string[] = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, '0'),
);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Fuente de aleatoriedad mínima que existe tanto en Node como en React
 * Native. Se tipa estructuralmente para no depender de las librerías DOM:
 * este paquete compila sin `lib: ["DOM"]` precisamente para garantizar que
 * no se cuela nada específico del navegador.
 */
interface RandomSource {
  getRandomValues<T extends Uint8Array>(array: T): T;
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  const g = globalThis as { crypto?: RandomSource };
  if (typeof g.crypto?.getRandomValues === 'function') {
    g.crypto.getRandomValues(bytes);
    return bytes;
  }
  /* c8 ignore start — sólo en entornos sin WebCrypto */
  throw new Error(
    'No hay una fuente de aleatoriedad criptográfica disponible (crypto.getRandomValues)',
  );
  /* c8 ignore stop */
}

export type UuidV7Generator = (now?: number) => string;

/**
 * Crea un generador con su propio estado monótono.
 *
 * La monotonía necesita estado (el último milisegundo y su contador). Ese
 * estado es por proceso, no global del módulo: así los tests pueden aislarse
 * y, sobre todo, el motor de sincronización puede tener un generador
 * dedicado sin que otro código le mueva el reloj lógico por debajo.
 */
export function createUuidV7Generator(): UuidV7Generator {
  let lastTimestamp = -1;
  let lastCounter = 0;

  return function generate(now: number = Date.now()): string {
    const bytes = randomBytes(16);

    // Contador monótono para colisiones dentro del mismo milisegundo.
    if (now === lastTimestamp) {
      lastCounter = (lastCounter + 1) & 0x0fff;
      if (lastCounter === 0) {
        // Desbordamiento del contador: avanzamos el reloj lógico un ms.
        lastTimestamp = now + 1;
        now = lastTimestamp;
      }
    } else if (now > lastTimestamp) {
      lastTimestamp = now;
      lastCounter = ((bytes[6]! & 0x0f) << 8) | bytes[7]!;
    } else {
      // Reloj hacia atrás (NTP, cambio manual): no retrocedemos nunca.
      // Es el caso 6 de SYNC-ENGINE.md §10: un terminal con la hora mal
      // puesta no puede corromper el orden de los identificadores.
      now = lastTimestamp;
      lastCounter = (lastCounter + 1) & 0x0fff;
    }

    const ts = BigInt(now);
    bytes[0] = Number((ts >> 40n) & 0xffn);
    bytes[1] = Number((ts >> 32n) & 0xffn);
    bytes[2] = Number((ts >> 24n) & 0xffn);
    bytes[3] = Number((ts >> 16n) & 0xffn);
    bytes[4] = Number((ts >> 8n) & 0xffn);
    bytes[5] = Number(ts & 0xffn);

    // versión 7 + 12 bits de contador
    bytes[6] = 0x70 | ((lastCounter >> 8) & 0x0f);
    bytes[7] = lastCounter & 0xff;

    // variante RFC 4122 (10xxxxxx)
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;

    let out = '';
    for (let i = 0; i < 16; i++) {
      out += HEX[bytes[i]!];
      if (i === 3 || i === 5 || i === 7 || i === 9) out += '-';
    }
    return out;
  };
}

/** Generador por defecto del proceso. */
export const uuidv7: UuidV7Generator = createUuidV7Generator();

/** Valida el formato de un UUID (cualquier versión). */
export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** Extrae el instante de creación de un UUID v7. `null` si no es v7. */
export function uuidv7Timestamp(uuid: string): Date | null {
  if (!isUuid(uuid)) return null;
  const hex = uuid.replace(/-/g, '');
  if (hex[12] !== '7') return null;
  return new Date(Number(BigInt(`0x${hex.slice(0, 12)}`)));
}

/**
 * Token opaco para etiquetas QR (SECURITY.md §7).
 *
 * No guarda ninguna relación con el UUID interno del equipo ni revela
 * empresa, cliente o centro. 128 bits de entropía en base62 (22 caracteres),
 * revocable sin tocar el activo.
 */
const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export function generateQrToken(): string {
  const bytes = randomBytes(16);
  let n = 0n;
  for (const b of bytes) n = (n << 8n) | BigInt(b);
  let out = '';
  while (out.length < 22) {
    out = BASE62[Number(n % 62n)] + out;
    n /= 62n;
  }
  return out;
}
